import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { isRouteErrorResponse, useRouteError, useRouteLoaderData } from "react-router";
import React from "react";
import { $routeId } from "safe-routes";
import { match, P } from "ts-pattern";

import type { loader as rootLoader } from "~/root";

type Status = number | undefined;

const getErrorTitle = (status: Status) => {
  switch (status) {
    case 404:
      return "Oops, page not found";
    case 403:
      return "Oops, you don't have access";
    default:
      return "Oops, something went wrong";
  }
};

const getErrorSubtitle = (status: Status) => {
  switch (status) {
    case 404:
      return "Sorry for the inconvenience. Click the chat icon in the lower-right and message our support team if you think this message is in error.";
    case 403:
      return "Sorry for the inconvenience. Click the chat icon in the lower-right and message our support team if you think this message is in error.";
    default:
      return "We're sorry for the inconvenience. Please try refreshing the page. If the problem keeps happening, click the chat icon in the lower-left to chat with our support team.";
  }
};

export function DefaultErrorBoundary() {
  const error = useRouteError();
  const status = isRouteErrorResponse(error) ? error.status : undefined;
  const title = getErrorTitle(status);
  const subtitle = getErrorSubtitle(status);
  const rootData = useRouteLoaderData<typeof rootLoader>($routeId("root"));
  const showErrorDetails = rootData?.isSynthesizeEmployee ?? false;

  return ErrorRender({ title, subtitle, showErrorDetails, error: error as Error });
}

type ErrorRenderProps = {
  title: string;
  subtitle: string;
  showErrorDetails: boolean;
  error: Error;
};
function ErrorRender({ title, subtitle, showErrorDetails, error }: ErrorRenderProps) {
  const errorDetails = match(error)
    .with(P.instanceOf(Error), (e) => (
      <div className="overflow-wrap-anywhere">
        <p className="text-sm text-destructive">{e.message}</p>
        <pre className="text-sm whitespace-pre-wrap">{e.stack}</pre>
      </div>
    ))
    .otherwise(() => <pre className="text-sm">{JSON.stringify(error, null, 2)}</pre>);
  return (
    <div className="flex flex-col items-center gap-4 mt-10 max-w-2xl mx-auto">
      <div className="flex items-center justify-center gap-3">
        <ExclamationCircleIcon className="h-10 w-10 text-gray-600" />
        <div className="text-center text-3xl font-bold text-gray-600">{title}</div>
      </div>
      <p className="text-lg">{subtitle}</p>
      {showErrorDetails && (
        <div className="w-full">
          <p>Error Details</p>
          {errorDetails}
        </div>
      )}
    </div>
  );
}

type ErrorBoundaryProps = {
  children: React.ReactNode;
  fallback: React.ReactNode;
};

export class ErrorBoundary extends React.Component<ErrorBoundaryProps> {
  state: { hasError: boolean; error: Error | undefined } = {
    hasError: false,
    error: undefined,
  };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return ErrorRender({
        title: "Oops, something went wrong",
        subtitle:
          "We're sorry for the inconvenience. Please try refreshing the page. If the problem keeps happening, click the chat icon in the lower-left to chat with our support team.",
        showErrorDetails: true,
        error: this.state.error ?? new Error("Unknown error"),
      });
    }
    return this.props.children;
  }
}
